import { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useReferenceLists } from "../../context/GlobalGetLists";
import type { Contact, ContactLink } from "../../types/deal";
import {
  apiDelete,
  apiPost,
  apiPut,
  type ApiResponse,
} from "../../services/genesisApi/httpsMethods";
import { CustomButton } from "../../components/common/customButton";
import { ConfirmDialog } from "../../components/common/confirmDialog";
import { SimpleToolbar } from "../../components/layout/Toolbar";
import { DynamicForm } from "../../components/common/DynamicForm";
import { contactFieldConfig } from "../../config/contactFieldConfig";
import { renderSubTitle } from "../../components/renderComponents";
import { CustomSingleSelect } from "../../components/common/inputFields/customSingleSelect";
import { CustomTextInput } from "../../components/common/inputFields/customTextInput";
import DeleteIcon from "@mui/icons-material/Delete";
import { CustomIconButton } from "../../components/common/customIconButton";
import { useCurrentUser } from "../../context/CurrentUserContext";
import { useErrorDialog } from "../../hooks/useErrorDialog";
import { ErrorDialog } from "../../components/common/errorDialog";

interface CreateContactProps {
  contact?: Partial<Contact>;
  isEdit?: boolean;
}

const emptyContact: Partial<Contact> = {
  contact_name: "",
  contact_email: "",
  contact_location: "",
  contact_phone: "",
  contact_title: "",
  company: "",
  notes: "",
};

type LinkTargetType = "Sponsor" | "Deal" | "Source";

type ContactLinkDraft = {
  id?: number | null;
  target_type: LinkTargetType;
  sponsor_id: number | null;
  entity_id: number | null;
  deal_id: number | null;
  source_id: number | null;
  role: string;
};

const emptyLinkDraft = (): ContactLinkDraft => ({
  target_type: "Sponsor",
  sponsor_id: null,
  entity_id: null,
  deal_id: null,
  source_id: null,
  role: "",
});

/** Builds the API payload for a link, or returns null if it does not target exactly one parent. */
const buildLinkPayload = (link: ContactLinkDraft) => {
  // Enforce exactly one parent
  const sponsor_id = link.target_type === "Sponsor" ? link.sponsor_id : null;
  const deal_id = link.target_type === "Deal" ? link.deal_id : null;
  const source_id = link.target_type === "Source" ? link.source_id : null;

  const parentCount = [sponsor_id, deal_id, source_id].filter(
    (v) => typeof v === "number" && v > 0,
  ).length;
  if (parentCount !== 1) return null;

  return {
    role: (link.role || "").trim() || null,
    sponsor_id,
    entity_id: null,
    deal_id,
    source_id,
  };
};

const CreateContact = ({ contact: initial, isEdit }: CreateContactProps) => {
  const { canEdit } = useCurrentUser();
  const { sponsors, deals, sources } = useReferenceLists();
  const [contact, setContact] = useState<Partial<Contact>>(
    initial ?? emptyContact,
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();

  const [links, setLinks] = useState<ContactLinkDraft[]>([]);
  const [deletedLinkIds, setDeletedLinkIds] = useState<number[]>([]);
  const [linksError, setLinksError] = useState<string | null>(null);
  const { errorDialog, showError, showErrorFromResponse, hideError } =
    useErrorDialog();

  useEffect(() => {
    // Seed unified links (preferred). If absent, start empty (legacy sponsor_links still exists but we won't auto-migrate).
    const incoming = (initial as any)?.links as ContactLink[] | undefined;
    if (incoming && Array.isArray(incoming) && incoming.length) {
      setLinks(
        incoming.map((l: any) => ({
          id: typeof l.id === "number" ? l.id : null,
          target_type: l.sponsor_id ? "Sponsor" : l.deal_id ? "Deal" : "Source",
          sponsor_id: l.sponsor_id ?? null,
          entity_id: l.entity_id ?? null,
          deal_id: l.deal_id ?? null,
          source_id: l.source_id ?? null,
          role: l.role ?? "",
        })),
      );
    } else {
      setLinks([]);
    }
  }, [initial]);

  const updateContact = (field: string, value: any) => {
    const val = value === "" || value === undefined ? null : value;
    setContact((prev) => ({ ...prev, [field]: val }));
  };

  const handleSubmit = async () => {
    try {
      setLinksError(null);
      if (isEdit && !contact.id) return;

      // Validate every link before sending anything, so an invalid link can never
      // leave a half-saved contact behind.
      const linkPayloads = links.map(buildLinkPayload);
      if (linkPayloads.some((payload) => payload === null)) {
        setLinksError(
          "Each link must target exactly one of Sponsor, Deal, or Source.",
        );
        return;
      }

      // Once the contact has been created (contact.id set below), a retry updates it
      // instead of creating it again.
      const existingId = contact.id;
      const response = existingId
        ? await apiPut("contact", existingId, contact)
        : await apiPost("contacts", contact);

      if (!response || !response.success) {
        console.error(
          "Contact save failed:",
          response?.message || "Unknown error",
        );
        showErrorFromResponse(response);
        return;
      }

      const contactId: number | undefined =
        existingId ?? (response.data as any)?.id;
      if (!contactId) {
        showError(
          undefined,
          "The contact was saved but no contact ID was returned, so its links could not be saved.",
        );
        return;
      }
      if (!existingId) {
        setContact((prev) => ({ ...prev, id: contactId }));
      }

      // Persist unified contact links
      const failures: ApiResponse<unknown>[] = [];

      const remainingDeletes: number[] = [];
      for (const linkId of deletedLinkIds) {
        const result = await apiDelete("contact-link", linkId);
        // 404 means the link is already gone, which is the outcome we wanted.
        if (!result.success && result.status !== 404) {
          remainingDeletes.push(linkId);
          failures.push(result);
        }
      }
      setDeletedLinkIds(remainingDeletes);

      const createdLinkIds = new Map<ContactLinkDraft, number>();
      for (const [idx, link] of links.entries()) {
        const payload = { ...linkPayloads[idx], contact_id: contactId };
        const result = link.id
          ? await apiPut("contact-link", link.id, payload)
          : await apiPost("contact-link", payload);

        if (!result.success) {
          failures.push(result);
          continue;
        }
        const newId = (result.data as any)?.id;
        if (!link.id && typeof newId === "number") {
          createdLinkIds.set(link, newId);
        }
      }
      if (createdLinkIds.size) {
        // Remember ids of newly created links so a retry updates them.
        setLinks((prev) =>
          prev.map((l) =>
            createdLinkIds.has(l) ? { ...l, id: createdLinkIds.get(l) } : l,
          ),
        );
      }

      if (failures.length > 0) {
        const summary = `The contact was saved, but ${failures.length} link change${
          failures.length === 1 ? "" : "s"
        } could not be saved. Click Update Contact to retry.`;
        const firstMessage = failures[0].message;
        const detail =
          typeof firstMessage === "string"
            ? firstMessage
            : firstMessage?.message;
        setLinksError(summary);
        showError(failures[0], detail ? `${summary} (${detail})` : summary);
        return;
      }

      navigate(`/contacts/${contactId}`);
    } catch (error) {
      console.error("Error saving contact:", error);
    }
  };

  const addLink = () => setLinks((prev) => [...prev, emptyLinkDraft()]);

  const updateLink = (idx: number, patch: Partial<ContactLinkDraft>) => {
    setLinks((prev) => {
      const next = prev.slice();
      if (!next[idx]) return prev;
      next[idx] = { ...next[idx], ...patch };

      // If target type changes, clear the other parent ids.
      if (patch.target_type) {
        if (patch.target_type === "Sponsor") {
          next[idx].deal_id = null;
          next[idx].source_id = null;
        } else if (patch.target_type === "Deal") {
          next[idx].sponsor_id = null;
          next[idx].source_id = null;
        } else if (patch.target_type === "Source") {
          next[idx].sponsor_id = null;
          next[idx].deal_id = null;
        }
      }

      return next;
    });
  };

  const removeLink = (idx: number) => {
    setLinks((prev) => {
      const next = prev.slice();
      const removed = next[idx];
      next.splice(idx, 1);
      if (removed?.id) {
        setDeletedLinkIds((ids) => [...ids, removed.id as number]);
      }
      return next;
    });
  };

  return (
    <Box sx={{ position: "relative", zIndex: 1 }}>
      <Box sx={{ position: "sticky", zIndex: 10, marginBottom: "10px" }}>
        <SimpleToolbar>
          {initial && (
            <CustomButton
              handleButtonPress={() => navigate(`/contacts/${contact.id}`)}
              isButtonDisabled={false}
              solid={false}
              text="Cancel"
              data-position="left"
            />
          )}
          <CustomButton
            handleButtonPress={handleSubmit}
            isButtonDisabled={!canEdit || !contact.contact_name}
            text={contact.id ? "Update Contact" : "Create Contact"}
            data-position="left"
          />
          <ConfirmDialog
            open={dialogOpen}
            endpoint="contact"
            id={contact.id!}
            title="Delete Contact"
            description="Are you sure you want to delete this contact? This action cannot be undone."
            confirmText="Delete"
            cancelText="Cancel"
            onClose={() => setDialogOpen(false)}
            onSuccess={() => navigate("/contacts")}
          />
          {isEdit && (
            <CustomButton
              handleButtonPress={() => setDialogOpen(true)}
              isButtonDisabled={!canEdit}
              text="Delete Contact"
              isDeleteButton
              data-position="right"
            />
          )}
        </SimpleToolbar>
      </Box>

      <Box sx={{ maxWidth: "95%", margin: "0 auto", padding: 4 }}>
        <DynamicForm
          config={contactFieldConfig}
          values={contact}
          onChange={updateContact}
        />

        {renderSubTitle("Links")}
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          {linksError ? (
            <Typography sx={{ color: "#BA2C2C", fontSize: "12px" }}>
              {linksError}
            </Typography>
          ) : null}

          {links.map((link, idx) => {
            return (
              <Box
                key={`link-${link.id ?? `new-${idx}`}`}
                sx={{ border: "1px solid #E0E0E0", borderRadius: 2, p: 2 }}
              >
                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  <Box sx={{ width: 180 }}>
                    <CustomSingleSelect
                      value={link.target_type}
                      onChange={(e) =>
                        updateLink(idx, {
                          target_type: e.target.value as LinkTargetType,
                        })
                      }
                      options={["Sponsor", "Deal", "Source"]}
                      placeholder="Select link type"
                      isStringArray={true}
                      fullWidth={true}
                    />
                  </Box>

                  {link.target_type === "Sponsor" ? (
                    <Box sx={{ flex: 1 }}>
                      <CustomSingleSelect
                        value={link.sponsor_id || ""}
                        onChange={(e) =>
                          updateLink(idx, {
                            sponsor_id: e.target.value || null,
                          })
                        }
                        options={sponsors}
                        placeholder="Select Sponsor"
                        labelKey="sponsor_name"
                        fullWidth={true}
                      />
                    </Box>
                  ) : null}

                  {link.target_type === "Deal" ? (
                    <Box sx={{ flex: 1 }}>
                      <CustomSingleSelect
                        value={link.deal_id || ""}
                        onChange={(e) =>
                          updateLink(idx, { deal_id: e.target.value || null })
                        }
                        options={deals}
                        placeholder="Select Deal"
                        labelKey="deal_name"
                        fullWidth={true}
                      />
                    </Box>
                  ) : null}

                  {link.target_type === "Source" ? (
                    <Box sx={{ flex: 1 }}>
                      <CustomSingleSelect
                        value={link.source_id || ""}
                        onChange={(e) =>
                          updateLink(idx, { source_id: e.target.value || null })
                        }
                        options={sources}
                        placeholder="Select Source"
                        labelKey="source_name"
                        fullWidth={true}
                      />
                    </Box>
                  ) : null}

                  <Box sx={{ width: 240 }}>
                    <CustomTextInput
                      label="Role"
                      value={link.role}
                      onChange={(val) =>
                        updateLink(idx, { role: String(val ?? "") })
                      }
                      fullWidth={true}
                    />
                  </Box>

                  <CustomIconButton
                    icon={<DeleteIcon />}
                    onClick={() => removeLink(idx)}
                  />
                </Box>
              </Box>
            );
          })}

          <Box sx={{ display: "flex", gap: 2 }}>
            <CustomButton
              text="Add Link"
              handleButtonPress={addLink}
              isButtonDisabled={!canEdit}
              solid={false}
            />
          </Box>
        </Box>
      </Box>

      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </Box>
  );
};

export default CreateContact;
